import { useToast, Modal, ModalContent, ModalOverlay, useColorModeValue } from '@chakra-ui/react'
import { AgentForm, FormData } from '../forms/AgentForm'
import { useAgents } from '../../hooks/useAgents'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { CreateAgentData } from '../../api/modules/agentApi'

interface CreateAgentDialogProps {
  isOpen: boolean
  onClose: () => void
}

const CreateAgentDialog: React.FC<CreateAgentDialogProps> = ({ isOpen, onClose }) => {
  const toast = useToast()
  const { t } = useLanguage()
  const { createAgent, isCreatingAgent } = useAgents()

  // Color mode values
  const modalBg = useColorModeValue('white', 'gray.900')

  const handleSubmit = async (data: FormData) => {
    try {
      const { fileSearchEnabled, ...rest } = data
      const agentData: CreateAgentData = {
        ...rest,
        type: 'normal',
        tools: fileSearchEnabled ? ['FILE_SEARCH'] : [],
      }
      const result = await createAgent(agentData)
      toast({
        title: t('createAgent.agentCreated'),
        description: `${result.name} ${t('createAgent.createdSuccessfully')}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      // Close the dialog
      onClose()
    } catch (error) {
      toast({
        title: t('createAgent.creationError'),
        description: t('createAgent.creationErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error // Re-throw to let the form handle the error state
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay bg="blackAlpha.300" />
      <ModalContent bg={modalBg} m={0} borderRadius={0}>
        <AgentForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isCreatingAgent}
          title={t('createAgent.title')}
          subtitle={t('createAgent.subtitle')}
          submitButtonText={t('createAgent.saveAgent')}
        />
      </ModalContent>
    </Modal>
  )
}

export default CreateAgentDialog
